'use client';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
// CONSTANTS
import { GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS } from '@/constants';

export type BuildBoxStep = 1 | 2 | 3 | 4;

export type Product = {
  sku: string;
  qty: number;
  name: string;
  image: string;
  price: number;
  isGiftHamper: boolean;
  stockQty: number;
  specialPrice?: number;
  stock_status?: 'IN_STOCK' | 'OUT_OF_STOCK';
};

export type SelectedBox = {
  sku: string;
  name: string;
  price: number;
  specialPrice?: number;
  imageUrl: string;
  image: string;
  boxCapacity: number;
  minItemsRequired: number;
  options: {
    title: 'recipient_name' | 'greeting_card' | 'gift_message';
    option_id: number;
  }[];
};

export type PersonalizationData = {
  recipientName: string;
  giftMessage: string;
  greetingCard?: {
    id: number;
    name: string;
    price: string;
    sku: string;
    imageUrl: string;
  };
};

interface MakeYourGiftState {
  steps: { id: BuildBoxStep; label: string; route: string }[];
  getStepByRoute: (route: string) => BuildBoxStep | null;

  // selected box state
  selectedBox: SelectedBox | null;
  selectBox: (box: SelectedBox | null) => void;
  journeyStatus: 'not-started' | 'started' | 'complete';
  setJourneyStatus: (
    journeyStatus: 'not-started' | 'started' | 'complete'
  ) => void;

  // new product map state
  productsMap: Record<string, Product>;
  totalItemsSelected: number;
  totalPrice: number;
  addProduct: (product: Product) => void;
  removeProduct: (sku: string) => void;
  updateProduct: (sku: string, qty: number) => void;
  clearProducts: () => void;
  setProductsMap: (data: Record<string, Product>) => void;

  // personalization state
  personalizationData: PersonalizationData | null;
  isPersonalizationAdded: () => boolean;
  setPersonalizationData: (data: Partial<PersonalizationData>) => void;
  clearPersonalizationData: () => void;
  removeBox: () => void;
  // capacity helper
  isBoxCapacityReached: () => boolean;

  // event-based sync method
  giftBoxInCart: any | null;
  syncFromCartItem: (giftBoxItem: any, cartItems: any[]) => void;
  syncPersonalizationData: ({
    personalizationData,
    boxOptions,
  }: {
    personalizationData?: PersonalizationData | null;
    boxOptions: {
      title: 'recipient_name' | 'greeting_card' | 'gift_message';
      option_id: number;
    }[];
  }) => void;
}

export const useMakeYourGiftStore = create<MakeYourGiftState>()(
  persist(
    (set, get) => {
      const recalcTotals = (productsMap: Record<string, Product>) => {
        const totalItems = Object.values(productsMap).reduce(
          (acc, p) => acc + p.qty,
          0
        );
        const boxSpecialPrice = get().selectedBox?.specialPrice;
        const boxPrice = get().selectedBox?.price ?? 0;

        const greetingCardPrice = Number(
          get().personalizationData?.greetingCard?.price ?? 0
        );
        const totalBoxPrice = boxSpecialPrice ?? boxPrice;
        const totalPrice =
          Object.values(productsMap).reduce((acc, p) => {
            const unitPrice = p.specialPrice ?? p.price;
            return acc + unitPrice * p.qty;
          }, 0) +
          totalBoxPrice +
          greetingCardPrice;
        return { totalItems, totalPrice };
      };

      return {
        steps: [
          {
            id: 1,
            label: 'Select Box',
            route: GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS[0],
          },
          {
            id: 2,
            label: 'Add Items',
            route: GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS[1],
          },
          {
            id: 3,
            label: 'Personalize',
            route: GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS[2],
          },
          {
            id: 4,
            label: 'Summary',
            route: GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS[3],
          },
        ],

        getStepByRoute: (route: string) => {
          const step = get().steps.find(s => {
            // Handle exact match first
            if (s.route === route) return true;

            // Handle augmented routes (e.g., route: /gifting/build-your-own-box/add-items/cheese/)
            // Check if the route starts with the step route and has additional path segments
            if (route.startsWith(s.route) && route.length > s.route.length) {
              // The route starts with the step route, so it's a match for augmented routes
              return true;
            }

            return false;
          });
          return step ? step.id : null;
        },

        // --- selected box state ---
        selectedBox: null,
        selectBox: box => {
          set({ selectedBox: box });
          const { totalItems, totalPrice } = recalcTotals(get().productsMap);
          set({ totalItemsSelected: totalItems, totalPrice });
        },
        removeBox: () => {
          set({ selectedBox: null });
          const emptyMap: Record<string, Product> = {};
          set({ productsMap: emptyMap });
          set({ personalizationData: null });
          set({ totalItemsSelected: 0, totalPrice: 0 });
        },

        // --- product map state ---
        productsMap: {},
        totalItemsSelected: 0,
        totalPrice: 0,
        journeyStatus: 'not-started',
        setJourneyStatus: (value: 'not-started' | 'started' | 'complete') =>
          set(() => ({ journeyStatus: value })),
        addProduct: product =>
          set(state => {
            const updated = {
              ...state.productsMap,
              [product.sku]: product,
            };
            const { totalItems, totalPrice } = recalcTotals(updated);
            return {
              productsMap: updated,
              totalItemsSelected: totalItems,
              totalPrice,
            };
          }),

        removeProduct: sku =>
          set(state => {
            const updated = { ...state.productsMap };
            delete updated[sku];
            const { totalItems, totalPrice } = recalcTotals(updated);
            return {
              productsMap: updated,
              totalItemsSelected: totalItems,
              totalPrice,
            };
          }),

        updateProduct: (sku, qty) =>
          set(state => {
            if (!state.productsMap[sku]) return state;
            const updated = {
              ...state.productsMap,
              [sku]: {
                ...state.productsMap[sku],
                qty,
              },
            };
            const { totalItems, totalPrice } = recalcTotals(updated);
            return {
              productsMap: updated,
              totalItemsSelected: totalItems,
              totalPrice,
            };
          }),

        clearProducts: () =>
          set(() => {
            const emptyMap: Record<string, Product> = {};
            const { totalItems, totalPrice } = recalcTotals(emptyMap);
            return {
              productsMap: emptyMap,
              totalItemsSelected: totalItems,
              totalPrice,
            };
          }),

        // --- personalization state ---
        personalizationData: null,

        setPersonalizationData: (data: Partial<PersonalizationData>) => {
          const hasGreetingCardUpdate = Object.prototype.hasOwnProperty.call(
            data,
            'greetingCard'
          );

          // First, merge personalization data
          set(state => ({
            personalizationData: state.personalizationData
              ? { ...state.personalizationData, ...data }
              : (data as PersonalizationData),
          }));

          // Then, if greeting card changed, recalc totals
          if (hasGreetingCardUpdate) {
            const { totalItems, totalPrice } = recalcTotals(get().productsMap);
            set({ totalItemsSelected: totalItems, totalPrice });
          }
        },

        clearPersonalizationData: () => set({ personalizationData: null }),

        isPersonalizationAdded: () => {
          const { personalizationData } = get();
          const { recipientName, giftMessage, greetingCard } =
            personalizationData ?? {};
          return Boolean(recipientName || giftMessage || greetingCard?.sku);
        },

        // --- capacity helper ---
        isBoxCapacityReached: () => {
          const boxCapacity = get().selectedBox?.boxCapacity ?? 3;
          const totalItems = Object.values(get().productsMap).reduce(
            (acc, p) => acc + p.qty,
            0
          );
          return totalItems >= boxCapacity;
        },
        setProductsMap: data => set({ productsMap: data }),

        // --- event-based sync method ---
        giftBoxInCart: null,
        syncFromCartItem: (giftBoxItem: any, cartItems: any[]) => {
          if (!giftBoxItem || !cartItems) return;

          // Store the gift box item in localStorage
          set({ giftBoxInCart: giftBoxItem });

          // Get gift box products using the same logic as getGiftBoxProducts
          const giftBoxProducts = cartItems.filter(cartItem => {
            const isGiftBoxItem =
              String(cartItem?.extension_attributes?.gift_box_item_id) ===
              String(giftBoxItem?.item_id);
            const isNotGiftBox =
              cartItem?.extension_attributes?.is_gift_box !== '1';
            const isNotGreetingCard =
              cartItem?.extension_attributes?.is_greeting_card !== 1;
            return isGiftBoxItem && isNotGiftBox && isNotGreetingCard;
          });

          // Sync the gift box data
          const boxData = {
            sku: giftBoxItem.sku,
            name: giftBoxItem.name,
            price: giftBoxItem.price || 0,
            specialPrice: undefined, // Could be extracted from custom attributes if available
            imageUrl: giftBoxItem.extension_attributes?.product_image || '',
            image: `${process.env.NEXT_PUBLIC_CDN_LINK}/media/catalog/product${giftBoxItem.extension_attributes?.product_image || ''}`,
            boxCapacity: Number(
              (giftBoxItem.extension_attributes as any)?.no_of_items || 0
            ),
            minItemsRequired: Number(
              (giftBoxItem.extension_attributes as any)?.min_no_of_items || 0
            ),
            options: [],
          };

          const productsMap = giftBoxProducts.reduce(
            (acc, product) => {
              acc[product.sku] = {
                sku: product.sku,
                qty: product.qty,
                name: product.name,
                image: `${process.env.NEXT_PUBLIC_CDN_LINK}/media/catalog/product${product.extension_attributes?.product_image || ''}`,
                price: product.price || 0,
                isGiftHamper:
                  product.extension_attributes?.is_gift_hamper === '1',
                stockQty: product.extension_attributes?.available_qty || 0,
                specialPrice: undefined, // Could be extracted from custom attributes if available
                stock_status:
                  product.extension_attributes?.in_stock === 1
                    ? 'IN_STOCK'
                    : 'OUT_OF_STOCK',
              };
              return acc;
            },
            {} as Record<string, Product>
          );
          get().setJourneyStatus('started');
          get().selectBox(boxData);
          get().setProductsMap(productsMap);
          // Manually recalculate totals after setting products map
          const { totalItems, totalPrice } = recalcTotals(productsMap);
          set({ totalItemsSelected: totalItems, totalPrice });
        },
        syncPersonalizationData: ({
          personalizationData,
          boxOptions,
        }: {
          personalizationData?: PersonalizationData | null;
          boxOptions: {
            title: 'recipient_name' | 'greeting_card' | 'gift_message';
            option_id: number;
          }[];
        }) => {
          const selectedBox = get().selectedBox;
          if (selectedBox) {
            get().selectBox({ ...selectedBox, options: boxOptions });
          }
          get().setPersonalizationData(personalizationData ?? {});
        },
      };
    },
    {
      name: 'make-your-gift-stepper',
      partialize: state => ({
        selectedBox: state.selectedBox,
        productsMap: state.productsMap,
        journeyStatus: state.journeyStatus,
        totalItemsSelected: state.totalItemsSelected,
        totalPrice: state.totalPrice ?? 0,
        personalizationData: state.personalizationData,
        giftBoxInCart: state.giftBoxInCart,
      }),
    }
  )
);

export default useMakeYourGiftStore;
